/** @jsx jsx */
import React, { useMemo, useState, useCallback, useEffect } from 'react'
import { jsx } from 'theme-ui'
import { css } from '@emotion/react'
import replace from 'string-replace-to-array'
import { times } from 'lodash'

import Button, {
  gradColors,
  randomGradColor,
  randomGradColorArray,
} from '../button'
import Buttons from '../buttons'
import Paragraph from '../paragraph'

import { fillArea, floodBgImage } from '../../styles/css'
import { Cont, VStack } from '../../styles/els'
import { renderHTML, resolveButtons } from '../../lib/helpers'
import { motion, AnimatePresence } from 'framer-motion'

const regexPattern = /\[\$(.*?)\]/g

const Slide = ({ heading, paragraph, buttons: _buttons }) => {
  const buttons = resolveButtons(_buttons)
  const colors = useMemo(() => {
    const count = ((paragraph || '').match(regexPattern) || []).length
    return randomGradColorArray(count)
  }, [paragraph])

  let highlightCount = 0

  return (
    <VStack space={6}>
      {heading && <Paragraph heading={heading} hTier={1} align="center" />}
      <VStack space={8}>
        <div
          sx={{
            variant: 'text.regular_28',
            textAlign: 'center',
            maxWidth: '860px',
            mx: 'auto',
          }}
        >
          {paragraph
            ? replace(
                paragraph,
                regexPattern,
                (fullString, containedString, offset) => {
                  const _i = highlightCount
                  const _color = colors[_i % colors.length]

                  highlightCount += 1
                  return (
                    <Button
                      label={containedString}
                      key={_i}
                      sx={{ display: 'inline' }}
                      compact={true}
                      color={_color}
                    />
                  )
                }
              )
            : null}
        </div>
        {buttons && buttons.length > 0 ? (
          <Buttons buttons={buttons} align="center" />
        ) : null}
      </VStack>
    </VStack>
  )
}

const Arrow = ({ onClick, right = false }) => {
  return (
    <div
      sx={{
        position: 'absolute',
        left: right ? null : '-16px',
        right: right ? '-16px' : null,
        top: '50%',
        width: '32px',
        height: '32px',
        mt: '-16px',
        borderRadius: '16px',
        backgroundColor: 'b5',
        color: 'b1',
        transform: right ? null : 'scaleX(-1)',
        transition: 'background 0.3s ease-in-out',

        '&:hover': {
          cursor: 'pointer',
          backgroundColor: 'b9',
        },
      }}
      onClick={onClick}
    >
      <svg
        id="Layer_1"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 56.27 92.54"
        sx={{
          width: '12px',
          height: 'auto',
          display: 'block',
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate3d(-50%,-50%,0)',
        }}
      >
        <path
          d="m10,92.54c-2.56,0-5.12-.98-7.07-2.93-3.9-3.91-3.9-10.24,0-14.14l29.2-29.2L2.93,17.07c-3.91-3.91-3.9-10.24,0-14.14,3.9-3.9,10.24-3.9,14.14,0l36.27,36.27c3.9,3.91,3.9,10.24,0,14.14l-36.27,36.27c-1.95,1.95-4.51,2.93-7.07,2.93Z"
          fill="currentColor"
        />
      </svg>
    </div>
  )
}

const ActivatedParagraph = (props) => {
  const { paragraphs, ...otherProps } = props
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)

  const shiftIndex = useCallback(
    (_shift = 1) => {
      const shift = Number.isFinite(_shift) ? _shift : 1

      let newIndex = activeSlideIndex + shift

      if (newIndex >= paragraphs.length) {
        newIndex = 0
      } else if (newIndex < 0) {
        newIndex = paragraphs.length - 1
      }

      setActiveSlideIndex(newIndex)
    },
    [activeSlideIndex, paragraphs?.length]
  )

  useEffect(() => {
    const to = setTimeout(() => {
      shiftIndex()
    }, 7000)

    return () => {
      clearTimeout(to)
    }
  }, [activeSlideIndex])

  return paragraphs && paragraphs.length > 0 ? (
    <div
      sx={{
        position: 'relative',
        px: '32px',
      }}
    >
      <AnimatePresence mode="wait">
        {paragraphs && paragraphs[activeSlideIndex] && (
          <motion.div
            key={activeSlideIndex}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={{
              visible: {
                opacity: 1,
                transition: {
                  duration: 0.6,
                },
              },
              hidden: {
                opacity: 0,
                transition: {
                  duration: 0.3,
                },
              },
            }}
          >
            <Slide {...paragraphs[activeSlideIndex]} />
          </motion.div>
        )}
      </AnimatePresence>
      {paragraphs && paragraphs.length > 1 && (
        <>
          <Arrow
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()

              shiftIndex(-1)
            }}
          />
          <Arrow
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()

              shiftIndex(1)
            }}
            right={true}
          />
        </>
      )}
    </div>
  ) : null
}

export default ActivatedParagraph
